import { parseEditorContent } from '@components/ArtBoard/Comments/components/Comment/components/Content/utils'
import { useClickOutside } from '@lib/hooks'
import { type EditorValue, NoteEditor } from '@octopus-do/octopus-editor'
import {
  type ID,
  ProjectActions,
  type StickyNote,
} from '@octopus-do/octopus-project-core'
import { commitProjectRequest } from '@storeOld/actionCreators'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import cx from 'classnames'
import type React from 'react'
import {
  type FocusEvent,
  type MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import Draggable from 'react-draggable'
import { useDispatch } from 'react-redux'
import { Resizable } from 'react-resizable'

import { isEditorValue, isInsidePopover } from '@/utils'
import { StickyNotesItemActionsToolbar } from './components/StickyNotesItemActionsToolbar'
import { useDrag, useFocusState, useNoteStyle, useResize } from './hooks'
import css from './StickyNotesItem.module.scss'

type Props = {
  disabled: boolean
  readOnly?: boolean
  data: StickyNote
  parentId: ID
}

const StickyNotesItem: React.FC<Props> = ({
  disabled,
  readOnly = false,
  data,
  parentId,
}) => {
  const dispatch = useDispatch()

  const contentRef = useRef<HTMLDivElement>(null)

  const { id, content: stickyNoteItemContent, color } = data
  // Notes saved before OCT-5065 have no textSize; 's' is the 12px they were drawn at.
  const textSize = data.textSize ?? 's'

  // Ключ перемонтирует редактор при внешнем обновлении заметки, поэтому он
  // должен быть строкой: контент из публичного API приходит массивом, а такой
  // ключ всегда сводился к `[object Object]`.
  const contentKey = isEditorValue(stickyNoteItemContent)
    ? JSON.stringify(stickyNoteItemContent)
    : stickyNoteItemContent

  const [actualState, setContent] = useState(stickyNoteItemContent)

  const { isSelected, isEditing, isActionsOpen, handleMouseUp, handleBlur } =
    useFocusState(disabled || readOnly, data.id, () => {})

  const { isDragging, handleResetDragging, nodeRef, ...draggableProps } =
    useDrag(data.id, parentId, disabled || readOnly, isEditing)

  const { onContentUpdated, ...resizableProps } = useResize(
    contentRef,
    data.id,
    disabled || readOnly,
    isSelected
  )
  const noteStyle = useNoteStyle(
    color,
    isDragging,
    isEditing,
    disabled || readOnly,
    resizableProps
  )

  const wrapperClassName = cx(css.wrapper, {
    [css.selected]: isSelected,
    [css.interactive]: !disabled && !readOnly,
  })

  const clickOutsideProps = useClickOutside<HTMLDivElement>(isSelected, (e) => {
    if (isInsidePopover(e.target)) {
      return
    }

    handleBlur()
    handleResetDragging()

    if (disabled || readOnly) {
      return
    }

    dispatch(
      commitProjectRequest(
        ProjectActions.stickyNotes.updateContent({ id, content: actualState })
      )
    )
  })

  const onChange = (value: EditorValue) => {
    onContentUpdated()
    setContent(JSON.stringify(value))
  }

  const onWrapperMouseUp = (e: MouseEvent<HTMLDivElement>) => {
    handleMouseUp(e, isDragging)
  }

  const onEditorFocus = (e: FocusEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).getAttribute('contenteditable') !== 'true') {
      return
    }

    dispatch({
      type: CanvasActionTypes.SELECT_TO_EDIT_STICKY_NOTE,
      payload: id,
    })
  }

  useEffect(() => {
    setContent(stickyNoteItemContent)
  }, [stickyNoteItemContent])

  return (
    <div
      {...clickOutsideProps}
      className={wrapperClassName}
      onMouseUp={onWrapperMouseUp}
      data-sticky-note="true"
    >
      {/* @ts-ignore */}
      <Draggable nodeRef={nodeRef} {...draggableProps} grid={[3, 3]}>
        <div
          className={cx(css.container, 'StickyNote-container')}
          style={noteStyle}
          data-node-type="sticky-note"
          id={data.id}
          ref={nodeRef as React.RefObject<HTMLDivElement>}
        >
          {/* @ts-ignore */}
          <Resizable {...resizableProps}>
            <>
              {isActionsOpen && (
                <StickyNotesItemActionsToolbar
                  color={color}
                  id={data.id}
                  textSize={textSize}
                />
              )}

              <div
                className={cx(css.content, css[textSize], {
                  'is-select-text': readOnly,
                })}
                ref={contentRef}
                onFocus={onEditorFocus}
              >
                <NoteEditor
                  value={parseEditorContent(stickyNoteItemContent)}
                  readonly={!isEditing || readOnly}
                  autofocus
                  placeholder="Write a note..."
                  onChange={onChange}
                  key={contentKey}
                />
              </div>
            </>
          </Resizable>
        </div>
      </Draggable>
    </div>
  )
}

export default StickyNotesItem
